import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { NAV_LINKS, getAppName, tenantConfig } from '@/lib/sovereign';
import { WalletButton } from './web3/wallet-button';
import { useAccess } from '@/hooks/use-access';
import { NotificationBell } from './notification-bell';
import { TenantLogo } from './tenant-logo';
import { PrivacyToggle } from './dashboard-layout';

const SUPER_ADMIN_WALLETS = (
  import.meta.env.VITE_SUPER_ADMIN_WALLETS ||
  import.meta.env.VITE_ADMIN_WALLETS ||
  ''
).split(',').map((w: string) => w.trim().toLowerCase()).filter(Boolean);

function LockIcon() {
  return (
    <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function AdminDropdown({ isAdmin, isSuperAdmin, location }: { isAdmin: boolean; isSuperAdmin: boolean; location: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isOnAdminPage = location === '/admin' || location === '/super-admin' || location === '/franchise/dashboard';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border transition-colors",
          isOnAdminPage
            ? 'bg-brand/15 text-brand border-brand/30'
            : 'bg-zinc-800 text-slate-300 border-zinc-700 hover:bg-zinc-700'
        )}
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {isSuperAdmin ? 'Manage' : 'Panel'}
        <svg className={cn("w-3 h-3 transition-transform", open && "rotate-180")} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 rounded-xl border border-zinc-700 bg-zinc-900 shadow-xl shadow-black/40 py-1 z-50">
          {isAdmin && isSuperAdmin && (
            <Link
              href="/admin"
              onClick={() => setOpen(false)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm transition-colors",
                location === '/admin'
                  ? 'text-brand bg-brand/10'
                  : 'text-slate-300 hover:bg-zinc-800 hover:text-white'
              )}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Admin Panel
            </Link>
          )}
          {isAdmin && !isSuperAdmin && (
            <Link
              href="/franchise/dashboard"
              onClick={() => setOpen(false)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm transition-colors",
                location === '/franchise/dashboard'
                  ? 'text-brand bg-brand/10'
                  : 'text-slate-300 hover:bg-zinc-800 hover:text-white'
              )}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              My Dashboard
            </Link>
          )}
          {isSuperAdmin && (
            <Link
              href="/franchise/dashboard"
              onClick={() => setOpen(false)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm transition-colors",
                location === '/franchise/dashboard'
                  ? 'text-brand bg-brand/10'
                  : 'text-slate-300 hover:bg-zinc-800 hover:text-white'
              )}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Franchise Dashboard
            </Link>
          )}
          {isSuperAdmin && (
            <Link
              href="/super-admin"
              onClick={() => setOpen(false)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm transition-colors",
                location === '/super-admin'
                  ? 'text-amber-400 bg-amber-900/20'
                  : 'text-amber-400/80 hover:bg-amber-900/15 hover:text-amber-400'
              )}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Super Admin
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export function SovereignNav() {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { level, isConnected, isPremium, isAdmin, address } = useAccess();
  const hasFullAccess = level === 'full';
  const isSuperAdmin = !!address && SUPER_ADMIN_WALLETS.includes(address.toLowerCase());

  const visibleNavLinks = NAV_LINKS.filter(link => {
    if (!link.featureGate) return true;
    return tenantConfig.features[link.featureGate] !== false;
  });

  return (
    <header className="sticky top-0 z-50 w-full border-b border-zinc-800 bg-zinc-950/90 backdrop-blur-md">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-brand focus:text-zinc-900 focus:rounded-lg focus:font-semibold focus:text-sm"
      >
        Skip to content
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/" className="flex items-center gap-2 group" aria-label="Go to Home">
            <TenantLogo size={36} className="rounded-lg group-hover:border-brand/60 transition-colors" />
            <span className="hidden sm:block font-bold text-lg text-slate-50 group-hover:text-brand transition-colors">
              {getAppName()}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {visibleNavLinks.map((link) => {
              const isActive = link.href === '/dashboard'
                ? location.startsWith('/dashboard')
                : location === link.href;
              const isLocked = link.protected && !hasFullAccess;

              return (
                <Link
                  key={link.href}
                  href={link.href}
                  title={isLocked ? 'Connect wallet and add a sub-wallet to unlock' : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-brand/10 text-brand border border-brand/30'
                      : isLocked
                      ? 'text-zinc-400 hover:text-zinc-400 cursor-pointer'
                      : 'text-slate-400 hover:text-slate-50 hover:bg-zinc-800'
                  )}
                >
                  {link.label}
                  {isLocked && <><LockIcon /><span className="sr-only">(locked)</span></>}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {isConnected && location.startsWith('/dashboard') && (
              <div className="hidden sm:block">
                <PrivacyToggle />
              </div>
            )}
            {isConnected && (
              <NotificationBell />
            )}
            {isConnected && !isPremium && (
              <Link href="/subscribe" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-brand/10 text-brand border border-brand/30 hover:bg-brand/20 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                Upgrade
              </Link>
            )}
            {isPremium && !isAdmin && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-brand/15 text-brand border border-brand/20">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Premium
              </span>
            )}
            {(isAdmin || isSuperAdmin) && (
              <div className="hidden sm:block">
                <AdminDropdown isAdmin={isAdmin} isSuperAdmin={!!isSuperAdmin} location={location} />
              </div>
            )}
            <div className="hidden sm:block">
              <WalletButton variant="outline" size="sm" />
            </div>
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg text-slate-400 hover:text-slate-50 hover:bg-zinc-800 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {mobileOpen ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div id="mobile-nav" className="md:hidden border-t border-zinc-800 bg-zinc-950">
          <nav className="flex flex-col px-4 py-3 gap-1" aria-label="Mobile navigation">
            {visibleNavLinks.map((link) => {
              const isActive = link.href === '/dashboard'
                ? location.startsWith('/dashboard')
                : location === link.href;
              const isLocked = link.protected && !hasFullAccess;

              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-brand/10 text-brand border border-brand/30'
                      : isLocked
                      ? 'text-zinc-400'
                      : 'text-slate-400 hover:text-slate-50 hover:bg-zinc-800'
                  )}
                >
                  <span>{link.label}</span>
                  {isLocked && (
                    <span className="flex items-center gap-1 text-xs text-zinc-700">
                      <LockIcon />
                      Unlock
                    </span>
                  )}
                </Link>
              );
            })}
            {isAdmin && (
              <Link
                href="/admin"
                onClick={() => setMobileOpen(false)}
                className={cn(
                  'flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                  location === '/admin'
                    ? 'bg-brand/10 text-brand border border-brand/30'
                    : 'text-slate-400 hover:text-slate-50 hover:bg-zinc-800'
                )}
              >
                <span>Admin Panel</span>
              </Link>
            )}
            {isSuperAdmin && (
              <Link
                href="/super-admin"
                onClick={() => setMobileOpen(false)}
                className={cn(
                  'flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                  location === '/super-admin'
                    ? 'bg-brand/10 text-brand border border-brand/30'
                    : 'text-amber-400 hover:text-amber-300 hover:bg-amber-900/20'
                )}
              >
                <span>Super Admin</span>
              </Link>
            )}
            <div className="pt-2 pb-1">
              <WalletButton variant="outline" size="sm" fullWidth />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
